import {
    PairedSingleAndReturnAmenitiesType
} from "@CheckOrder/features/amenities/types/PairedSingleAndReturnAmenities.type";
import useCountAmenityValueGetter
    from "@CheckOrder/features/amenities/features/countAmenity/hooks/useCountAmenityValueGetter";
import useSumAmenities from "@CheckOrder/features/amenities/hooks/useSumAmenities";
import IconType from "@/types/Icon.type";
import {useAppSelector} from "@Hooks/selector/useAppSelector";
import useSelectedAmenities
    from "@CheckOrder/features/amenities/features/countAmenity/features/list/hooks/useSelectedAmenities";

/**
 * returns functions for creating selected amenity summaries
 */
export default function useCreateSelectedAmenitiesSummary(pairedAmenityGroups: PairedSingleAndReturnAmenitiesType) {
    const {getCountAmenityValue} = useCountAmenityValueGetter()
    const {sumAmenities} = useSumAmenities()

    const {isReturn} = useAppSelector((state) => (state.checkOrder.ticket))

    const {
        selectedSingleAmenities,
        selectedReturnAmenities,
    } = useSelectedAmenities(pairedAmenityGroups)

    /**
     * returns only amenity keys whose amenities have been added
     */
    const getValidAmenityKeys = () => (pairedAmenityGroups.amenityKeys.filter((amenityKey) => (
        selectedSingleAmenities[amenityKey] || selectedReturnAmenities[amenityKey]
    )))

    /**
     * creates description and total price per single amenity
     */
    const createSummaryPerAmenityKey = (amenityKey: string) => {
        const description: string[] = []
        let icon: IconType = null
        let totalPricePerAmenity = 0

        if (selectedSingleAmenities[amenityKey]) {
            description.push(
                `${getCountAmenityValue(selectedSingleAmenities[amenityKey], "single")}x ${selectedSingleAmenities[amenityKey].translations.title}${isReturn ? " (outbound)" : ""}`
            )
            icon = selectedSingleAmenities[amenityKey].icon
            totalPricePerAmenity += sumAmenities([selectedSingleAmenities[amenityKey]], "single")
        }
        if (selectedReturnAmenities[amenityKey]) {
            description.push(
                `${getCountAmenityValue(selectedReturnAmenities[amenityKey], "return")}x ${selectedReturnAmenities[amenityKey].translations.title} (return)`
            )
            icon = selectedReturnAmenities[amenityKey].icon
            totalPricePerAmenity += sumAmenities([selectedReturnAmenities[amenityKey]], "return")
        }

        return {
            description: description.join(" ,"),
            icon,
            totalPricePerAmenity,
        }
    }

    const createAllDescriptionsArray = () => (
        getValidAmenityKeys().map((amenityKey) => (createSummaryPerAmenityKey(amenityKey).description))
    )

    return {
        getValidAmenityKeys,
        createSummaryPerAmenityKey,
        createAllDescriptionsArray,
    }
}